import { useState } from 'react';
import TextoRico from '@/components/base/TextoRico';
import type { Pergunta } from '../types';

interface PreviaFormularioProps {
  nome: string;
  perguntas: Pergunta[];
  aoFechar: () => void;
}

function perguntaVisivel(p: Pergunta, respostas: Record<string, unknown>): boolean {
  if (!p.condicaoChave) return true;
  const resposta = respostas[p.condicaoChave];
  if (p.condicaoValor === 'true') return resposta === 'true';
  if (p.condicaoValor === 'false') return resposta === 'false';
  if (Array.isArray(resposta)) return resposta.includes(p.condicaoValor);
  return resposta === p.condicaoValor;
}

export default function PreviaFormulario({ nome, perguntas, aoFechar }: PreviaFormularioProps) {
  const [respostas, setRespostas] = useState<Record<string, unknown>>({});

  function responder(chave: string, valor: unknown) {
    setRespostas((prev) => ({ ...prev, [chave]: valor }));
  }

  function alternarMultipla(chave: string, opcao: string) {
    setRespostas((prev) => {
      const atual = Array.isArray(prev[chave]) ? (prev[chave] as string[]) : [];
      const novo = atual.includes(opcao) ? atual.filter((o) => o !== opcao) : [...atual, opcao];
      return { ...prev, [chave]: novo };
    });
  }

  const visiveis = perguntas.filter((p) => perguntaVisivel(p, respostas));

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/50 px-4 py-8">
      <div className="flex max-h-full w-full max-w-[560px] flex-col overflow-hidden rounded-lg bg-white">
        <div className="flex items-center justify-between border-b border-secondary-200 px-5 py-4">
          <div className="min-w-0">
            <h3 className="truncate text-base font-medium text-foreground-950">
              {nome || 'Formulário'}
            </h3>
            <p className="text-xs text-secondary-500">Pré-visualização · como o participante vê</p>
          </div>
          <button
            type="button"
            onClick={aoFechar}
            aria-label="Fechar"
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-md text-secondary-500 transition-colors hover:bg-background-100"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        <div className="overflow-y-auto px-5 py-4">
          {visiveis.length === 0 ? (
            <p className="py-8 text-center text-sm text-secondary-500">
              Este formulário ainda não tem perguntas.
            </p>
          ) : (
            <div className="space-y-5">
              {visiveis.map((p) => (
                <div key={p.chave}>
                  <div className="flex flex-wrap items-baseline gap-x-1 text-[15px] font-medium text-foreground-950">
                    <TextoRico texto={p.enunciado} />
                    {p.obrigatoria && <span className="text-danger-600">*</span>}
                  </div>
                  {p.descricao && (
                    <TextoRico texto={p.descricao} className="mt-0.5 text-sm text-secondary-500" />
                  )}

                  <div className="mt-2">
                    {p.tipo === 'texto_curto' && (
                      <input
                        type="text"
                        className="w-full rounded-md border border-secondary-300 px-3 py-2 text-[15px] outline-none"
                        onChange={(e) => responder(p.chave, e.target.value)}
                      />
                    )}
                    {p.tipo === 'texto_longo' && (
                      <textarea
                        rows={3}
                        className="w-full resize-y rounded-md border border-secondary-300 px-3 py-2 text-[15px] outline-none"
                        onChange={(e) => responder(p.chave, e.target.value)}
                      />
                    )}
                    {p.tipo === 'sim_nao' && (
                      <div className="flex gap-4">
                        {['true', 'false'].map((v) => (
                          <label key={v} className="flex cursor-pointer items-center gap-2 text-[15px]">
                            <input
                              type="radio"
                              name={`prev-${p.chave}`}
                              checked={respostas[p.chave] === v}
                              onChange={() => responder(p.chave, v)}
                            />
                            {v === 'true' ? 'Sim' : 'Não'}
                          </label>
                        ))}
                      </div>
                    )}
                    {p.tipo === 'escolha_unica' && (
                      <div className="space-y-1">
                        {p.opcoes.map((o) => (
                          <label key={o} className="flex cursor-pointer items-center gap-2 text-[15px]">
                            <input
                              type="radio"
                              name={`prev-${p.chave}`}
                              checked={respostas[p.chave] === o}
                              onChange={() => responder(p.chave, o)}
                            />
                            {o}
                          </label>
                        ))}
                      </div>
                    )}
                    {p.tipo === 'multipla_escolha' && (
                      <div className="space-y-1">
                        {p.opcoes.map((o) => (
                          <label key={o} className="flex cursor-pointer items-center gap-2 text-[15px]">
                            <input
                              type="checkbox"
                              checked={(respostas[p.chave] as string[])?.includes(o) || false}
                              onChange={() => alternarMultipla(p.chave, o)}
                            />
                            {o}
                          </label>
                        ))}
                      </div>
                    )}
                    {p.tipo === 'numero' && (
                      <input
                        type="number"
                        className="w-full rounded-md border border-secondary-300 px-3 py-2 text-[15px] outline-none"
                        onChange={(e) => responder(p.chave, e.target.value)}
                      />
                    )}
                    {p.tipo === 'data' && (
                      <input
                        type="date"
                        className="w-full cursor-pointer rounded-md border border-secondary-300 px-3 py-2 text-[15px] outline-none"
                        onChange={(e) => responder(p.chave, e.target.value)}
                      />
                    )}
                    {p.tipo === 'telefone' && (
                      <input
                        type="tel"
                        className="w-full rounded-md border border-secondary-300 px-3 py-2 text-[15px] outline-none"
                        onChange={(e) => responder(p.chave, e.target.value)}
                      />
                    )}
                    {p.tipo === 'email' && (
                      <input
                        type="email"
                        className="w-full rounded-md border border-secondary-300 px-3 py-2 text-[15px] outline-none"
                        onChange={(e) => responder(p.chave, e.target.value)}
                      />
                    )}
                    {p.tipo === 'cpf' && (
                      <input
                        type="text"
                        className="w-full rounded-md border border-secondary-300 px-3 py-2 text-[15px] outline-none"
                        onChange={(e) => responder(p.chave, e.target.value)}
                      />
                    )}
                    {p.tipo === 'lista' && (
                      <div className="rounded-md border border-secondary-300 px-3 py-2">
                        <p className="text-xs text-secondary-500">
                          Lista — cada linha tem uma caixinha por coluna:
                        </p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {p.opcoes.map((c) => (
                            <span
                              key={c}
                              className="rounded-full bg-secondary-100 px-2.5 py-1 text-xs text-secondary-700"
                            >
                              {c}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    {p.tipo === 'foto' && (
                      <div className="flex h-[140px] items-center justify-center rounded-md border border-dashed border-secondary-300 bg-background-50 px-4 text-center">
                        <span className="flex items-center gap-2 text-sm text-secondary-500">
                          <span className="flex h-5 w-5 items-center justify-center">
                            <i className="ri-camera-line" />
                          </span>
                          Campo de foto (tirar ou escolher)
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="border-t border-secondary-200 px-5 py-3">
          <button
            type="button"
            onClick={aoFechar}
            className="w-full cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-600"
          >
            Fechar pré-visualização
          </button>
        </div>
      </div>
    </div>
  );
}